import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ChevronsUpDown, Upload } from "lucide-react";
import { useMemo, useState } from "react";

import { Loading } from "@/components/ui/loading";
import { SkeletonLines } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Page, PageHeader, Toolbar } from "@/components/ui/page";
import { SearchInput } from "@/components/ui/search-input";
import { Segmented } from "@/components/ui/tabs";
import { api } from "@/services/api";
import type { Translate } from "@/constants/i18n";
import { cn } from "@/utils/utils";
import { downloadText } from "@/utils/download";
import { useTranslation } from "@/store/locale";

/** One tier's counts, with the legacy API's key spellings. */
interface Counts {
  business: number;
  gold: number;
  Affiliates: number;
  employee: number;
  complimentary: number;
  total: number;
}

interface ClubRow {
  club_id: string;
  /** Null for the handful of clubs legacy MRM left unnamed. */
  club_name: string | null;
  primary: Counts;
  Supplementary: Counts;
}

interface Analytics {
  member_data: ClubRow[];
}

interface Col {
  key: string;
  label: string;
  get: (counts: Counts) => number;
  /** The sum at the end of the line, weighted like legacy's. */
  total?: boolean;
}

/**
 * Legacy's `PRIMARY_TIER_COLUMNS`, in its order: the five type-and-tier pairs a
 * primary card can be, then the total.
 */
const primaryColumns = (t: Translate): Col[] => [
  { key: "business", label: t("an.business"), get: (c) => c.business },
  { key: "gold", label: t("an.goldMember"), get: (c) => c.gold },
  { key: "affiliates", label: t("an.affiliates"), get: (c) => c.Affiliates },
  { key: "complimentary", label: t("an.complimentary"), get: (c) => c.complimentary },
  { key: "employee", label: t("an.employee"), get: (c) => c.employee },
  { key: "total", label: t("an.total"), get: (c) => c.total, total: true },
];

/**
 * `SUPPLEMENTARY_TIER_COLUMNS`: the same list without Affiliates and Employee.
 *
 * Not blanked — absent. Neither tier issues a supplementary card, so a column
 * for them is a column of zeros that invites the reader to wonder what it
 * would take to fill it.
 */
const SUPPLEMENTARY_OMITS = new Set(["affiliates", "employee"]);
const supplementaryColumns = (t: Translate): Col[] =>
  primaryColumns(t).filter((c) => !SUPPLEMENTARY_OMITS.has(c.key));

const num = (value: number) => value.toLocaleString();

/**
 * What an unnamed club reads as. Five clubs came over from legacy MRM with no
 * name; a blank cell looks like a rendering fault, and sorting or filtering on
 * `null` throws outright.
 *
 * Module scope, not the page's: the table bodies below render in `Panel`, which
 * is a component of its own and never saw a helper closed over by the page.
 */
const label = (name: string | null) => name ?? "(unnamed club)";

type Dir = "asc" | "desc";
interface Sort {
  key: string;
  dir: Dir;
}
/** Which of the two detailed tables a click on a count column came from. */
type Side = "primary" | "supplementary";
interface DetailedSort extends Sort {
  from: Side;
}

function MemberAnalyticsPage() {
  const t = useTranslation();
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"detailed" | "summary">("detailed");
  // One sort for the pair of tables. They stand side by side and a club has to
  // be on the same line in both, or reading across them says nothing.
  const [detailed, setDetailed] = useState<DetailedSort>({
    key: "club_name",
    dir: "asc",
    from: "primary",
  });
  const [summary, setSummary] = useState<Sort>({ key: "club_name", dir: "asc" });

  const analytics = useQuery({
    queryKey: ["/member-analytics"],
    queryFn: async ({ signal }) =>
      (await api.get<Analytics>("/member-analytics", { signal })).data,
    staleTime: 60_000,
  });
  const found = useMemo(() => {
    const all = analytics.data?.member_data ?? [];
    const needle = search.trim().toLowerCase();
    return needle ? all.filter((r) => label(r.club_name).toLowerCase().includes(needle)) : all;
  }, [analytics.data, search]);

  const primary = useMemo(() => primaryColumns(t), [t]);
  const supplementary = useMemo(() => supplementaryColumns(t), [t]);
  // An empty table is two different pieces of news: a search that found
  // nothing, or a side with nothing on it. Legacy said which.
  const searching = search.trim() !== "";

  /**
   * The club order both detailed tables share.
   *
   * Sorting by a column of one table reorders the other with it — they are two
   * halves of one reading, and a club that moved in one and not the other would
   * put unrelated numbers on the same line. Legacy did the same, by sorting the
   * table that was clicked and then reordering its neighbour to follow.
   *
   * Which numbers decide the order is the table the click came from, which is
   * why the sort carries `from`. Adding the two sides together instead put the
   * clubs in an order neither table showed: click Business on the supplementary
   * side and the club with the most supplementary business cards could sit
   * halfway down, outranked by a club whose primaries carried the sum.
   */
  const detailedRows = useMemo(() => {
    const col = primary.find((c) => c.key === detailed.key);
    const sign = detailed.dir === "asc" ? 1 : -1;
    const side = (row: ClubRow) =>
      detailed.from === "primary" ? row.primary : row.Supplementary;
    return [...found].sort((a, b) =>
      col
        ? (col.get(side(a)) - col.get(side(b))) * sign
        : label(a.club_name).localeCompare(label(b.club_name)) * sign,
    );
  }, [found, detailed, primary]);

  const summaryRows = useMemo(() => {
    const totals = found.map((r) => ({
      club_id: r.club_id,
      club_name: r.club_name,
      primary: r.primary.total,
      supplementary: r.Supplementary.total,
      total: r.primary.total + r.Supplementary.total,
    }));
    const sign = summary.dir === "asc" ? 1 : -1;
    const by = summary.key as "primary" | "supplementary" | "total";
    return totals.sort((a, b) =>
      summary.key === "club_name"
        ? label(a.club_name).localeCompare(label(b.club_name)) * sign
        : (a[by] - b[by]) * sign,
    );
  }, [found, summary]);

  const sum = (rows: ClubRow[], col: Col, of: (r: ClubRow) => Counts) =>
    rows.reduce((acc, row) => acc + col.get(of(row)), 0);

  /** Legacy's two files, each headed the way its own view reads. */
  const exportCsv = () => {
    // Legacy's `escapeCsvCell`: quote a cell only when it holds a comma, a
    // quote or a newline, and double the quotes inside it. Wrapping every cell
    // and leaving the quotes inside alone — which is what this did — writes a
    // broken file the moment a club is called something like 5" Club, because
    // the unescaped quote ends the field early and shifts the rest of the line.
    const quote = (rows: (string | number)[][]) =>
      rows
        .map((line) =>
          line
            .map((v) => {
              const cell = String(v);
              return /[",\n\r]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
            })
            .join(","),
        )
        .join("\n");
    const stamp = new Date().toISOString().slice(0, 10);
    if (view === "summary") {
      const csv = quote([
        [t("an.clubName"), t("an.primary"), t("an.supplementary"), t("an.total")],
        ...summaryRows.map((r) => [label(r.club_name), r.primary, r.supplementary, r.total]),
      ]);
      return downloadText(csv, `member-analytics-summary-per-club-${stamp}.csv`);
    }
    const csv = quote([
      [t("an.primary")],
      [t("an.clubName"), ...primary.map((c) => c.label)],
      ...detailedRows.map((r) => [label(r.club_name), ...primary.map((c) => c.get(r.primary))]),
      [],
      [t("an.supplementary")],
      [t("an.clubName"), ...supplementary.map((c) => c.label)],
      ...detailedRows.map((r) => [
        label(r.club_name),
        ...supplementary.map((c) => c.get(r.Supplementary)),
      ]),
    ]);
    downloadText(csv, `member-analytics-detailed-summary-${stamp}.csv`);
  };

  return (
    <Page>
      {/* No actions on the heading: Export belongs to the search row below,
          where the two controls that act on the rows sit together. */}
      <PageHeader title={t("nav.memberAnalytics")} description={t("an.description")} />

      {/* `wrap-reverse` rather than a pair of `order`s. One line and it is the
          row the rest of the app writes — search at the left edge, the view
          control at the right. Wrapped, the lines stack bottom-up, so the
          control that decides what the page is showing lands on top and the
          search row under it, and the DOM stays in the order it reads in.
          Export travels with the search box either way: it writes out the rows
          the search leaves, so it belongs beside what decides them. */}
      <Toolbar className="flex flex-wrap-reverse items-center justify-between gap-3">
        <div className="flex w-full items-center gap-2.5 sm:w-auto">
          <SearchInput
            containerClassName="min-w-0 flex-1 sm:w-64 sm:flex-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch("")}
            placeholder={t("an.searchClub")}
            aria-label={t("an.searchClub")}
          />
          <Button variant="secondary" className="h-10" onClick={exportCsv}>
            <Upload aria-hidden /> {t("rp.export")}
          </Button>
        </div>
        {/* `h-10` here, `h-10` on Export, and the search box is a `md` field
            at 40 already: the three controls on this row are one height, so
            the row reads as one row. The track carries the height and its two
            buttons stretch inside it. */}
        <Segmented
          className="h-10 max-w-full overflow-x-auto"
          label={t("an.view")}
          value={view}
          onChange={setView}
          items={[
            { value: "detailed", label: t("an.detailedSummary") },
            { value: "summary", label: t("an.summaryPerClub") },
          ]}
        />
      </Toolbar>

      {analytics.isPending ? (
        <Loading busy action="loading" subject={t("nav.memberAnalytics")} className="mt-6">
          <SkeletonLines rows={6} />
        </Loading>
      ) : view === "detailed" ? (
        // Two panels, side by side on a wide screen and stacked on a narrow
        // one. Legacy's shape: a primary card and the cards issued under it are
        // different populations, counted differently — the supplementary side
        // has no affiliates and no employees at all — so they are two tables
        // rather than two lines of one.
        <div className="mt-4 grid gap-4 xl:grid-cols-2">
          <Panel
            title={t("an.primary")}
            side="primary"
            clubs={detailedRows.length}
            columns={primary}
            rows={detailedRows}
            counts={(r) => r.primary}
            sort={detailed}
            onSort={setDetailed}
            sum={(col) => sum(detailedRows, col, (r) => r.primary)}
            empty={searching ? t("an.noClubs") : t("an.noPrimaryData")}
            t={t}
          />
          <Panel
            title={t("an.supplementary")}
            side="supplementary"
            clubs={detailedRows.length}
            columns={supplementary}
            rows={detailedRows}
            counts={(r) => r.Supplementary}
            sort={detailed}
            onSort={setDetailed}
            sum={(col) => sum(detailedRows, col, (r) => r.Supplementary)}
            empty={searching ? t("an.noClubs") : t("an.noSupplementaryData")}
            t={t}
          />
        </div>
      ) : (
        // One line a club: what the two sides come to, and what that makes.
        <section className="bg-surface-raised rounded-card border-line mt-4 overflow-hidden border">
          <TableHead title={t("an.summaryPerClub")} clubs={summaryRows.length} t={t} />
          <div className="overflow-x-auto">
            <table className="text-callout w-full">
              <thead className="bg-surface-sunken text-left">
                <tr>
                  <Th
                    id="club_name"
                    label={t("an.clubName")}
                    align="left"
                    sort={summary}
                    onSort={setSummary}
                  />
                  {(["primary", "supplementary", "total"] as const).map((key) => (
                    <Th
                      key={key}
                      id={key}
                      label={t(`an.${key}` as Parameters<Translate>[0])}
                      align="right"
                      sort={summary}
                      onSort={setSummary}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {summaryRows.map((r) => (
                  <tr key={r.club_id} className="hairline-t hover:bg-surface-sunken">
                    <td className="text-fg px-3 py-2.5 font-medium whitespace-nowrap">
                      {label(r.club_name)}
                    </td>
                    <td className="text-fg px-3 py-2 text-right tabular-nums">
                      {num(r.primary)}
                    </td>
                    <td className="text-fg px-3 py-2 text-right tabular-nums">
                      {num(r.supplementary)}
                    </td>
                    <td className="text-fg bg-accent-soft/40 px-3 py-2 text-right font-bold tabular-nums">
                      {num(r.total)}
                    </td>
                  </tr>
                ))}
                {summaryRows.length === 0 ? (
                  <Empty span={4} text={searching ? t("an.noClubs") : t("an.noSummaryData")} />
                ) : null}
              </tbody>
              {summaryRows.length ? (
                <tfoot className="hairline-t bg-surface-sunken text-heading font-semibold">
                  <tr>
                    <td className="px-3 py-2.5">{t("an.allClubs")}</td>
                    {(["primary", "supplementary", "total"] as const).map((key) => (
                      <td key={key} className="px-3 py-2 text-right tabular-nums">
                        {num(summaryRows.reduce((acc, r) => acc + r[key], 0))}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </section>
      )}
    </Page>
  );
}

/** The bar above a table: what it counts, and over how many clubs. */
function TableHead({ title, clubs, t }: { title: string; clubs: number; t: Translate }) {
  return (
    <header className="hairline-b flex items-center justify-between px-4 py-3">
      <h2 className="text-heading text-subhead font-semibold tracking-wide uppercase">
        {title}
      </h2>
      <span className="bg-surface-sunken text-fg-muted text-footnote rounded-full px-2.5 py-1 tabular-nums">
        {t("an.clubCount").replace("{count}", String(clubs))}
      </span>
    </header>
  );
}

/** One of the two detailed tables. */
function Panel({
  title,
  side,
  clubs,
  columns,
  rows,
  counts,
  sort,
  onSort,
  sum,
  empty,
  t,
}: {
  title: string;
  /** Which side's numbers a click on one of these headings sorts by. */
  side: Side;
  clubs: number;
  columns: Col[];
  rows: ClubRow[];
  counts: (row: ClubRow) => Counts;
  sort: DetailedSort;
  onSort: (next: DetailedSort) => void;
  sum: (col: Col) => number;
  empty: string;
  t: Translate;
}) {
  // The headings do not know which table they are in; the panel does, and
  // stamps it on so the order follows the numbers the reader clicked.
  const sortBy = (next: Sort) => onSort({ ...next, from: side });
  return (
    <section className="bg-surface-raised rounded-card border-line overflow-hidden border">
      <TableHead title={title} clubs={clubs} t={t} />
      <div className="overflow-x-auto">
        <table className="text-callout w-full">
          <thead className="bg-surface-sunken text-left">
            <tr>
              <Th
                id="club_name"
                label={t("an.clubName")}
                align="left"
                sort={sort}
                onSort={sortBy}
                pinned
              />
              {columns.map((c) => (
                <Th
                  key={c.key}
                  id={c.key}
                  label={c.label}
                  align="right"
                  sort={sort}
                  onSort={sortBy}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.club_id} className="hairline-t hover:bg-surface-sunken">
                <td className="bg-surface-raised text-fg px-3 py-2.5 font-medium whitespace-nowrap sm:sticky sm:left-0 sm:z-10">
                  {label(r.club_name)}
                </td>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cn(
                      "text-fg px-3 py-2 text-right tabular-nums",
                      c.total && "bg-accent-soft/40 font-bold",
                    )}
                  >
                    {num(c.get(counts(r)))}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 ? <Empty span={columns.length + 1} text={empty} /> : null}
          </tbody>
          {rows.length ? (
            <tfoot className="hairline-t bg-surface-sunken text-heading font-semibold">
              <tr>
                <td className="bg-surface-sunken px-3 py-2.5 sm:sticky sm:left-0 sm:z-10">
                  {t("an.allClubs")}
                </td>
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2 text-right tabular-nums">
                    {num(sum(c))}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </section>
  );
}

/** A sortable column heading. */
function Th({
  id,
  label,
  align,
  sort,
  onSort,
  pinned,
}: {
  id: string;
  label: string;
  align: "left" | "right";
  sort: Sort;
  onSort: (next: Sort) => void;
  pinned?: boolean;
}) {
  const on = sort.key === id;
  return (
    <th
      scope="col"
      aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
      className={cn(
        "hairline-b px-3 py-2.5",
        align === "right" && "text-right",
        // Pinned from `sm` up only. A phone scrolls this table with a thumb
        // on the table itself, and a column frozen against the left edge is a
        // column the thumb keeps dragging the numbers back under: the club is
        // already the row's first cell, and on a screen this narrow the reader
        // scrolls back to it rather than reading across from it. Wider, the
        // pointer scrolls a bar and a frozen first column is what makes the
        // right-hand counts readable at all.
        pinned && "bg-surface-sunken sm:sticky sm:left-0 sm:z-10",
      )}
    >
      <button
        type="button"
        onClick={() => onSort({ key: id, dir: on && sort.dir === "asc" ? "desc" : "asc" })}
        className="group/sort text-fg-muted text-subhead hover:text-fg inline-flex items-center gap-1 font-medium transition-colors"
      >
        {label}
        {on ? (
          sort.dir === "asc" ? (
            <ArrowUp className="text-accent size-3" aria-hidden />
          ) : (
            <ArrowDown className="text-accent size-3" aria-hidden />
          )
        ) : (
          <ChevronsUpDown
            className="text-fg-subtle size-3 opacity-0 transition-opacity group-hover/sort:opacity-100"
            aria-hidden
          />
        )}
      </button>
    </th>
  );
}

function Empty({ span, text }: { span: number; text: string }) {
  return (
    <tr>
      <td colSpan={span} className="text-fg-muted px-3 py-10 text-center">
        {text}
      </td>
    </tr>
  );
}

export default MemberAnalyticsPage;
